<!--
Auf dieser Seite kann der Benutzer ein Zertifikat sperren.
-->
<template>
  <div>
    <h1>{{ t("title") }}</h1>
    <form v-if="!success">
      <!-- Seriennummer -->
      <b-form-group
        :description="t('serial_description')"
        :invalid-feedback="serialFeedback"
        :valid-feedback="serialFeedback"
        :state="serialState"
      >
        <label for="serial">
          {{ t("serial_label") }}
        </label>
        <b-form-input
          id="serial"
          v-model="serial"
          :placeholder="t('serial_placeholder')"
          :state="serialState"
          type="text"
          autocomplete="off"
          trim
          @input="checkSerial(serial)"
        ></b-form-input>
      </b-form-group>
      <!-- PIN -->
      <b-form-group
        :description="t('password_description')"
        :invalid-feedback="passwordFeedback"
        :valid-feedback="passwordFeedback"
        :state="passwordState"
      >
        <label for="password">
          {{ t("password_label") }}
        </label>
        <b-form-input
          id="password"
          v-model="password"
          :placeholder="t('password_placeholder')"
          :state="passwordState"
          type="password"
          autocomplete="current-password"
          @input="passwordState = null"
        ></b-form-input>
      </b-form-group>
      <!-- Sperrgrund -->
      <b-form-group
        :description="t('reason_description')"
        :invalid-feedback="('choose_reason')"
        :valid-feedback="reason !== null ? t('explanation_' + reason) : null"
        :state="reasonState"
      >
        <label for="reason">
          {{ t("reason_label") }}
        </label>
        <!-- not using b-form-select because of https://github.com/bootstrap-vue/bootstrap-vue/issues/7198 -->
        <select
          id="reason"
          v-model="reason"
          class="form-control"
          :class="{ 'is-valid': reasonState }"
          @input="reasonState = reason !== null"
          >
          <option v-for="opt in reasons" :key="opt.value" :value="opt.value" >
            {{ opt.text }}
          </option>
        </select>
      </b-form-group>
      <!-- OK-Button -->
      <b-button
        variant="primary"
        :disabled="!(password && serial)"
        @click="revoke"
        >{{ t("revoke") }}</b-button
      >
    </form>
    <div v-else>
      <!-- Erfolgreich Sperrantrag abgeschickt. -->
      <p>{{ t("crr_sent") }}</p>
      <!-- Zurück-Button -->
      <b-button variant="primary" @click="back">
        {{ t("back") }}
      </b-button>
    </div>
  </div>
</template>

<i18n>
    # Die i18n-Strings sind in der Reihenfolge aufgeführt, in der sie der Benutzer zu sehen bekommt:
    # Strings, die noch nicht abgenommen wurden, sind mit einem Warndreieck markiert:
    en:
      title: "Certificate revocation"

      serial_label: "Serial number of the certificate to be revoked"
      serial_description: "If the certificate you want to revoke was sent to you
        via email after certificate issuance, you can find the relevant serial
        number of the certificate in that email. Otherwise you can find the
        certificate serial number in the certificate details as they are
        displayed by the certificate viewer of your operating system or software
        application, e.g. email client software or web browser."
      serial_placeholder: "Please enter the certificate serial number of the
        certificate you wish to revoke."
      serial_error: "The serial number must be a decimal or hexadecimal number."
      serial_not_found: "There is no certificate with this serial number."

      password_label: "Revocation PIN"
      password_description: "Usually the revocation PIN was supplied during the
        certificate application process by the applicant. If you don't know the
        revocation PIN, please contact your local subscriber service."
      password_placeholder: "Please enter the revocation PIN of the certificate
        you wish to revoke."
      password_too_short: "The entered revocation PIN is too short."
      password_error: "The entered revocation PIN is not correct."
      wrong_serial_or_password: "There is no certificate with this serial
        number or the entered revocation PIN is not correct."
      certificate_expired: "The certificate has already expired and can no longer be revoked."

      reason_label: "Reason for revocation"
      reason_description: "Please check whether you need to provide information
       on the revocation reason, e.g. if the private key has been compromised.
       Please select the most appropriate revocation reason."
      choose_reason: "Please select a revocation reason from the list."

      unspecified: "Unspecified"
      keyCompromise: "Key Compromise"
      affiliationChanged: "Change of Affiliation"
      superseded: "Superseded"
      cessationOfOperation: "Cessation of Operation"
      explanation_0: "If the other reasons do not apply, you must select the
        revocation reason \"Unspecified\"."
      explanation_1: "You must select the revocation reason \"Key Compromise\"
        if you have reason to believe that the private key of your certificate
        has been compromised, e.g. if an unauthorised person has had access to
        the private key."
      explanation_3: "You should select the revocation reason
        \"Change of Affiliation\" if the name of your organisation or other
        organisational information in the certificate has changed."
      explanation_4: "You should select the revocation reason \"Superseded\"
        if you have requested a new certificate to replace your
        existing certificate."
      explanation_5: "You should select the revocation reason \"Cessation of
        Operation\" if you no longer own all domain names of the certificate or
        if you no longer use the certificate because you discontinue the website."

      revoke: "Revoke certificate"

      crr_sent: "Your certificate revocation request has been sent."
      back: "Back"

    de:
      title: "Zertifikat sperren"

      serial_label: "Seriennummer des zu sperrenden Zertifikats"
      serial_description: "Sofern Sie bei der Ausstellung des zu sperrenden
        Zertifikats eine E-Mail mit dem Zertifikat bekommen haben, finden Sie
        die fragliche Seriennummer in dieser E-Mail. Ansonsten entnehmen Sie
        die Seriennummer des zu sperrenden Zertifikats bitte dessen
        Zertifikatdetails wie sie von der Zertifikatanzeige Ihres
        Betriebssystems bzw. Ihrer Anwendungs-Software (z.B. E-Mail-Programm
        oder Web-Browser) ausgegeben wird."
      serial_placeholder: "Geben Sie hier die Seriennummer des zu sperrenden
        Zertifikats ein."
      serial_error: "Die Seriennummer muss als Dezimal- oder Hexadezimalzahl
        eingegeben werden."
      serial_not_found: "Zu dieser Seriennummer existiert kein Zertifikat."

      password_label: "Sperr-PIN des Zertifikats"
      password_description: "Die Sperr-PIN wurde bei der Beantragung des
        Zertifikats von dem/der Beantragenden selbst gesetzt. Sofern Sie die
        Sperr-PIN nicht mehr kennen, wenden Sie sich bitte an Ihren lokalen
        Teilnehmerservice."
      password_placeholder: "Geben Sie hier die Sperr-PIN des zu sperrenden
        Zertifikats ein."
      password_too_short: "Die eingegebene Sperr-PIN ist zu kurz."
      password_error: "Die eingegebene Sperr-PIN ist leider nicht korrekt."
      wrong_serial_or_password: "Zu dieser Seriennummer existiert kein
        Zertifikat oder die eingegebene Sperr-PIN ist nicht korrekt."
      certificate_expired: "Das Zertifikat ist bereits abgelaufen und kann nicht mehr gesperrt werden."

      reason_label: "Sperrgrund"
      reason_description: "Bitte prüfen Sie, ob Sie Angaben zum Grund der Sperrung
       machen müssen, z.B. bei einer Kompromittierung des privaten Schlüssels.
       Bitte wählen Sie den passendsten Sperrgrund aus."
      choose_reason: "Bitte wählen Sie einen Sperrgrund aus der Liste."

      unspecified: "Ohne Angabe"
      keyCompromise: "Schlüssel kompromittiert"
      affiliationChanged: "Änderung der Zugehörigkeit"
      superseded: "Ersetzt"
      cessationOfOperation: "Nicht mehr im Einsatz"
      explanation_0: "Wenn die anderen Gründe nicht zutreffen, müssen Sie den
        Sperrgrund \"Ohne Angabe\" wählen."
      explanation_1: "Sie müssen den Sperrgrund \"Schlüssel kompromittiert\"
        wählen, wenn Sie Grund zu der Annahme haben, dass der private Schlüssel
        Ihres Zertifikats kompromittiert wurde, z. B. wenn eine unbefugte
        Person Zugriff auf den privaten Schlüssel hatte. "
      explanation_3: "Sie sollten den Sperrgrund \"Änderung der Zugehörigkeit\"
        wählen, wenn sich der Name Ihrer Organisation oder andere
        organisatorische Informationen im Zertifikat geändert haben."
      explanation_4: "Sie sollten den Sperrgrund \"Ersetzt\" wählen, wenn
        sie ein neues Zertifikat beantragt haben, um Ihr bestehendes Zertifikat
        zu ersetzen."
      explanation_5: "Sie sollten den Sperrgrund \"Nicht mehr im Einsatz\" wählen,
        wenn Sie nicht mehr im Besitz aller Domainnamen des Zertifikats sind oder
        wenn Sie das Zertifikat nicht mehr verwenden, weil Sie die Website einstellen"

      revoke: "Zertifikat sperren"

      crr_sent: "Ihr Sperrantrag ist eingegangen und wird bearbeitet."
      back: "Zurück"
</i18n>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { custom } from "@/customization/customization";
import { DFNSOAPClient } from "@/soap/dfnsoapclient";
import { sha1, hexToDec } from "@/pki/dfnpki";
import { Logger } from "@/logger/logger";

const { t } = useI18n()

const logger = new Logger("RevokeCertificate");

const emit = defineEmits(['error-message'])
const props = defineProps({ certSerial: { type: String, default: null } })

const serial = ref(props.certSerial);
const serialState = ref(null);
const serialFeedback = ref(null);
const password = ref(null);
const passwordState = ref(null);
const passwordFeedback = ref(null);
const reason = ref(0);
const reasonState = ref(true); // feedback bei der Auswahl des Sperrgrunds direkt anzeigen
const success = ref(false); // True, wenn der CRR gesendet wurde.

const reasons = computed(() => {
    return [
      // diese Zuordnung könnte auch nach pki ausgelagert werden.
      // { value: null, text: t("choose_reason") },
      { value: 0, text: t("unspecified") },
      { value: 3, text: t("affiliationChanged") },
      { value: 1, text: t("keyCompromise") },
      { value: 4, text: t("superseded") },
      { value: 5, text: t("cessationOfOperation") },
    ];
})

/**
 * Überprüft die eingegebene Seriennummer und setzt State und Feedback.
 */
const checkSerial = () => {
  if (!serial.value.match(/^(0[xX])?[0-9a-fA-F][0-9a-fA-F:]+$/)) {
    serialFeedback.value = t("serial_error");
    serialState.value = false;
  } else {
    serialFeedback.value = null;
    serialState.value = true;
  }
}

/**
 * Prüft die Inputs, ob sie sinnvolle Eingaben enthalten und setzt den
 * entsprechenden Status sowie das Feedback.
 *
 * @returns true gdw. alle Inputs sinnvolle Eingaben enthalten
 */
const checkInputs = () => {
  checkSerial();
  if (password.value.length < 8) {
    passwordFeedback.value = t("password_too_short");
    passwordState.value = false;
  } else {
    passwordFeedback.value = null;
    passwordState.value = true;
  }
  reasonState.value = reason.value !== null;

  return serialState.value && passwordState.value && reasonState.value;
}

/**
 * Setzt serial, password und reason auf 0 und success auf false.
 * Dadurch wird wieder ein leeres Formular angezeigt.
 */
const back = () => {
  success.value = false;
  serial.value = null;
  password.value = null;
  reason.value = 0;
  serialState.value = null;
  passwordState.value = null;
  reasonState.value = true; // feedback direkt anzeigen
}

const error = (msg) => {
  emit("error-message", msg);
}

/**
 * Überprüft die Inputs und stellt per SOAP einen Sperrantrag.
 */
const revoke = async () => {
  if (!checkInputs()) {
    // Inputs enthalten Fehler, Feedback ist schon gesetzt. --> Abbruch
    logger.error("Inputs enthalten Fehler.");
    return;
  }

  // Seriennummer nach dezimal wandeln, falls nicht schon dezimal.
  var decimalSerial = serial.value;
  if (!decimalSerial.match(/^[0-9]+$/)) {
    decimalSerial = hexToDec(decimalSerial.replace(/^0[xX]|:/g, ""));
  }

  logger.debug("Sperre Zertifikat " + decimalSerial + " / " + serial.value);
  const client = new DFNSOAPClient(custom.pubURL, custom.ra_id);
  try {
    const response = await client.newRevocationRequest(
      decimalSerial,
      reason.value,
      await sha1(password.value)
    );
    logger.info("CRR abgeschickt: " + response);
  } catch (e) {
    // Fehler von der SOAP-Schnittstelle
    if (e.message.match(/^Kein Zertifikat mit Seriennummer.+gefunden.$/)) {
      // Falsche Seriennummer
      serialFeedback.value = t("serial_not_found");
      serialState.value = false;
      passwordState.value = null;
    } else if (e.message === "Die PIN stimmt nicht.") {
      // Falsche PIN
      passwordFeedback.value = t("password_error");
      passwordState.value = false;
    } else if (e.message === "Das Zertifikat ist bereits abgelaufen und kann nicht mehr gesperrt werden.") {
      serialFeedback.value = t("certificate_expired");
      serialState.value = false;
      passwordState.value = null;
    } else {
      // Unbekannter Fehler
      logger.error("Unbekannter Fehler: " + e);
      // Damit wir keine Leere Fehlermeldung ausgeben, raten wir, dass PIN
      // oder Seriennummer falsch sind. Siehe #1745.
      error(t("wrong_serial_or_password"));
    }
    return;
  }
  // Kein Fehler --> Erfolgsseite anzeigen
  success.value = true;
}
</script>

<style scoped></style>
